<template>
  <div class="container p-4">
    <h3 class="text-xl font-bold mb-2">Videos</h3>
    <p class="text-sm text-600 mb-3">
      Filtra por curso, asignatura y (opcional) capítulo para ver los videos vinculados. Puedes crear videos, asociarlos a un capítulo y
      <strong>subir la carátula</strong> (imagen de portada), igual que las imágenes de las preguntas.
    </p>

    <CardPrime class="mb-4">
      <template #title>Filtros</template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div>
            <label class="block text-sm mb-1">Curso</label>
            <DropdownPrime
              v-model="selectedCursoId"
              :options="cursos"
              optionLabel="Glosa"
              optionValue="Id"
              placeholder="Todos"
              class="w-full"
              showClear
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Asignatura</label>
            <DropdownPrime
              v-model="selectedAsignaturaId"
              :options="asignaturas"
              optionLabel="Glosa"
              optionValue="Id"
              placeholder="Todos"
              class="w-full"
              showClear
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Capítulo (opcional)</label>
            <DropdownPrime
              v-model="selectedCapituloId"
              :options="capitulosFiltro"
              optionLabel="Glosa"
              optionValue="Id"
              placeholder="Todos"
              class="w-full"
              showClear
              :disabled="!puedeFiltrarCapitulos"
            />
          </div>
          <div>
            <label class="block text-sm mb-1">Glosa (contiene)</label>
            <InputText v-model="filtroGlosa" placeholder="Buscar..." class="w-full" @keyup.enter="aplicarFiltros" />
          </div>
          <div class="flex gap-2 flex-wrap">
            <ButtonPrime label="Aplicar" icon="pi pi-filter" @click="aplicarFiltros" />
            <ButtonPrime label="Nuevo video" icon="pi pi-plus" class="p-button-success" @click="abrirNuevo" />
          </div>
        </div>
      </template>
    </CardPrime>

    <DataTablePrime
      :value="videos"
      :loading="loading"
      class="p-datatable-sm"
      responsiveLayout="scroll"
      :emptyMessage="'Sin resultados'"
    >
      <ColumnPrime field="Id" header="ID" />
      <ColumnPrime field="Glosa" header="Glosa" />
      <ColumnPrime header="Curso">
        <template #body="{ data }">{{ data.Curso?.Glosa || data.Curso?.Id || '—' }}</template>
      </ColumnPrime>
      <ColumnPrime header="Asignatura">
        <template #body="{ data }">{{ data.Asignatura?.Glosa || data.Asignatura?.Id || '—' }}</template>
      </ColumnPrime>
      <ColumnPrime header="Capítulo" style="min-width: 12rem">
        <template #body="{ data }">{{ etiquetaCapitulo(data) }}</template>
      </ColumnPrime>
      <ColumnPrime field="IdVimeo" header="Vimeo" />
      <ColumnPrime header="Carátula" style="min-width: 5.5rem">
        <template #body="{ data }">
          <div v-if="urlCaratulaPublica(data.LinkImagenPortada)" class="caratula-thumb-wrap">
            <img
              :key="`thumb-${data.Id}-${revisionCaratula(data.Id)}`"
              class="caratula-thumb"
              :src="urlCaratulaConBust(data.LinkImagenPortada, data.Id)"
              alt=""
              loading="lazy"
              @load="onCaratulaImgLoad"
              @error="onCaratulaImgError"
            />
          </div>
          <span v-else class="text-600 text-sm">—</span>
        </template>
      </ColumnPrime>
      <ColumnPrime header="Vigente">
        <template #body="{ data }">{{ data.Vigente ? 'Sí' : 'No' }}</template>
      </ColumnPrime>
      <ColumnPrime header="Acciones" style="min-width: 220px">
        <template #body="{ data }">
          <ButtonPrime icon="pi pi-pencil" class="p-button-sm p-mr-1" title="Editar" @click="abrirEditar(data)" />
          <ButtonPrime
            icon="pi pi-image"
            class="p-button-sm p-button-help p-mr-1"
            :title="data.LinkImagenPortada ? 'Reemplazar carátula' : 'Subir carátula'"
            @click="abrirPortada(data)"
          />
          <ButtonPrime
            icon="pi pi-link"
            class="p-button-sm p-mr-1"
            title="Vincular a capítulo"
            :disabled="!puedeFiltrarCapitulos"
            @click="abrirVincular(data)"
          />
          <ButtonPrime
            v-if="data.Vigente"
            label="Desact."
            class="p-button-sm p-button-warning"
            @click="desactivar(data)"
          />
        </template>
      </ColumnPrime>
    </DataTablePrime>

    <div class="flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
      <span class="text-sm text-600">Página {{ page }} · {{ videos.length }} filas · total {{ totalElements }}</span>
      <div class="flex gap-2">
        <ButtonPrime label="Anterior" icon="pi pi-chevron-left" :disabled="page <= 1 || loading" @click="paginaAnterior" />
        <ButtonPrime label="Siguiente" icon="pi pi-chevron-right" iconPos="right" :disabled="loading || !hayMasPaginas" @click="paginaSiguiente" />
      </div>
    </div>

    <DialogPrime v-model:visible="dialogVideo" :modal="true" :header="tituloVideo" :style="{ width: '520px' }">
      <div class="mb-3 p-3 border-round surface-ground border-1 surface-border">
        <div class="text-sm font-semibold mb-2 text-700">Contexto del ingreso (según filtros de arriba)</div>
        <template v-if="puedeFiltrarCapitulos">
          <div class="text-sm mb-1">
            <span class="font-medium text-900">Curso:</span>
            {{ textoContextoCurso }}
          </div>
          <div class="text-sm mb-1">
            <span class="font-medium text-900">Asignatura:</span>
            {{ textoContextoAsignatura }}
          </div>
          <div class="text-sm">
            <span class="font-medium text-900">Capítulo (vinculación):</span>
            {{ textoContextoCapituloFormulario }}
          </div>
          <p class="text-xs text-600 mt-2 mb-0">
            El video se crea en el catálogo global; la vinculación a <strong>curso / asignatura / capítulo</strong> usa los filtros y el capítulo que elijas abajo (misma lógica que el listado).
          </p>
        </template>
        <p v-else class="text-sm text-600 mb-0">
          Selecciona <strong>curso</strong> y <strong>asignatura</strong> en los filtros superiores para ver aquí el contexto y poder elegir capítulo.
        </p>
      </div>
      <div class="field mb-3">
        <label class="block mb-1">Glosa / título</label>
        <InputText v-model="form.Glosa" class="w-full" />
      </div>
      <div v-if="puedeFiltrarCapitulos" class="field mb-3">
        <label class="block mb-1">Capítulo al que pertenece el video</label>
        <DropdownPrime
          v-model="form.capituloId"
          :options="capitulosFiltro"
          optionLabel="Glosa"
          optionValue="Id"
          placeholder="Sin vincular (elige capítulo)"
          class="w-full"
          showClear
          :disabled="!capitulosFiltro.length"
        />
        <small class="text-600">Usa los filtros <strong>Curso</strong> y <strong>Asignatura</strong> arriba para cargar la lista de capítulos.</small>
      </div>
      <p v-else class="text-sm text-600 mb-3">Selecciona curso y asignatura en los filtros superiores para poder elegir el capítulo.</p>
      <div class="grid grid-cols-2 gap-3">
        <div class="field mb-3">
          <label class="block mb-1">Orden</label>
          <InputNumber v-model="form.Orden" :min="0" class="w-full" />
        </div>
        <div class="field mb-3">
          <label class="block mb-1">ID Vimeo</label>
          <InputNumber v-model="form.IdVimeo" :min="0" class="w-full" />
        </div>
      </div>
      <div class="field mb-3 flex align-items-center">
        <CheckboxPrime v-model="form.Vigente" :binary="true" inputId="vid-vig" />
        <label for="vid-vig" class="ml-2">Vigente</label>
      </div>
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" @click="dialogVideo = false" />
        <ButtonPrime label="Guardar" @click="guardarVideo" />
      </template>
    </DialogPrime>

    <DialogPrime
      v-model:visible="dialogPortada"
      :modal="true"
      :header="portadaRow?.LinkImagenPortada ? 'Reemplazar carátula' : 'Subir carátula'"
      :style="{ width: '460px' }"
    >
      <p v-if="portadaRow" class="text-sm mb-2">
        <span class="text-600">Video</span> <strong>{{ portadaVideoId }}</strong>
        <span v-if="portadaRow.Glosa"> — {{ portadaRow.Glosa }}</span>
      </p>
      <p class="text-sm text-600 mb-2">JPG o PNG. Al subir un archivo nuevo se reemplaza la imagen en el servidor y en la base de datos.</p>
      <div v-if="urlCaratulaPublica(portadaRow?.LinkImagenPortada)" class="mb-3 p-2 border-round surface-ground border-1 surface-border">
        <div class="text-xs font-semibold text-600 mb-1">Vista previa actual</div>
        <img
          :key="`preview-${portadaVideoId}-${revisionCaratula(portadaVideoId)}-${portadaFileInputKey}`"
          class="caratula-preview"
          :src="urlCaratulaConBust(portadaRow.LinkImagenPortada, portadaVideoId)"
          alt="Carátula actual"
          @load="onCaratulaImgLoad"
          @error="onCaratulaImgError"
        />
      </div>
      <input
        :key="portadaFileInputKey"
        type="file"
        accept="image/png,image/jpeg,.jpg,.jpeg,.png"
        class="w-full mb-2"
        @change="onFilePortada"
      />
      <p v-if="archivoPortada" class="text-sm text-700 mb-0">Seleccionado: {{ archivoPortada.name }}</p>
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" :disabled="subiendoPortada" @click="dialogPortada = false" />
        <ButtonPrime
          label="Subir y guardar"
          icon="pi pi-upload"
          :loading="subiendoPortada"
          :disabled="!archivoPortada || subiendoPortada"
          @click="subirPortada"
        />
      </template>
    </DialogPrime>

    <DialogPrime v-model:visible="dialogVincular" :modal="true" header="Vincular video a capítulo" :style="{ width: '420px' }">
      <p class="text-sm mb-2">Video <strong>{{ vincularVideoId }}</strong></p>
      <label class="block mb-1">Capítulo</label>
      <DropdownPrime
        v-model="vincularCapituloId"
        :options="capitulosFiltro"
        optionLabel="Glosa"
        optionValue="Id"
        placeholder="Selecciona capítulo"
        class="w-full"
        :disabled="!capitulosFiltro.length"
      />
      <template #footer>
        <ButtonPrime label="Cancelar" class="p-button-secondary" @click="dialogVincular = false" />
        <ButtonPrime label="Vincular" @click="confirmarVincular" />
      </template>
    </DialogPrime>
  </div>
</template>

<script>
import CardPrime from 'primevue/card';
import DataTablePrime from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import DialogPrime from 'primevue/dialog';
import ButtonPrime from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import DropdownPrime from 'primevue/dropdown';
import CheckboxPrime from 'primevue/checkbox';
import CursoService from '@/services/CursoService';
import AsignaturaService from '@/services/AsignaturaService';
import CapituloUnidadService from '@/services/CapituloUnidadService';
import VideoService from '@/services/VideoService';
import { formatApiError } from '@/utils/formatApiError';

export default {
  name: 'MantenedorVideos',
  components: {
    CardPrime,
    DataTablePrime,
    ColumnPrime,
    DialogPrime,
    ButtonPrime,
    InputText,
    InputNumber,
    DropdownPrime,
    CheckboxPrime,
  },
  data() {
    return {
      cursos: [],
      asignaturas: [],
      capitulosFiltro: [],
      selectedCursoId: null,
      selectedAsignaturaId: null,
      selectedCapituloId: null,
      filtroGlosa: '',
      videos: [],
      loading: false,
      page: 1,
      pageSize: 25,
      totalElements: 0,
      totalPages: 1,
      dialogVideo: false,
      tituloVideo: 'Video',
      form: {
        Id: 0,
        Glosa: '',
        Vigente: true,
        Orden: 1,
        IdVimeo: 0,
        /** Capítulo seleccionado en ESTUDYOO_RELACION_VIDEO_CAPITULO (mismo curso/asignatura que filtros). */
        capituloId: null,
        /** Valor inicial al abrir edición (para saber si cambió la vinculación). */
        capituloIdInicial: null,
      },
      dialogPortada: false,
      portadaVideoId: null,
      /** Fila del listado al abrir el modal de carátula (glosa + link actual). */
      portadaRow: null,
      portadaFileInputKey: 0,
      archivoPortada: null,
      subiendoPortada: false,
      /** Base para query de caché cuando aún no hay revisión por video. */
      caratulaCacheBust: 0,
      /** Por id de video: timestamp tras subir carátula (misma ruta en servidor → fuerza nueva petición HTTP). */
      caratulaThumbRevision: {},
      dialogVincular: false,
      vincularVideoId: null,
      vincularCapituloId: null,
    };
  },
  computed: {
    puedeFiltrarCapitulos() {
      return this.selectedCursoId != null && this.selectedAsignaturaId != null;
    },
    hayMasPaginas() {
      return this.page < this.totalPages;
    },
    textoContextoCurso() {
      const id = this.selectedCursoId;
      if (id == null) return '—';
      const c = this.cursos.find((x) => x.Id === id);
      return c?.Glosa ? `${c.Glosa} (id ${id})` : `id ${id}`;
    },
    textoContextoAsignatura() {
      const id = this.selectedAsignaturaId;
      if (id == null) return '—';
      const a = this.asignaturas.find((x) => x.Id === id);
      return a?.Glosa ? `${a.Glosa} (id ${id})` : `id ${id}`;
    },
    /** Capítulo elegido en el formulario del modal (vinculación al guardar). */
    textoContextoCapituloFormulario() {
      if (this.form.capituloId == null) {
        return 'Ninguno aún — elige uno en el desplegable de abajo (opcional).';
      }
      const cap = this.capitulosFiltro.find((x) => x.Id === this.form.capituloId);
      const g = cap?.Glosa != null ? String(cap.Glosa).trim() : '';
      if (g && g !== '0') return `${g} (id ${this.form.capituloId})`;
      return `Capítulo id ${this.form.capituloId}`;
    },
  },
  watch: {
    selectedCursoId() {
      this.selectedCapituloId = null;
      this.capitulosFiltro = [];
      this.cargarCapitulosFiltro();
    },
    selectedAsignaturaId() {
      this.selectedCapituloId = null;
      this.capitulosFiltro = [];
      this.cargarCapitulosFiltro();
    },
  },
  methods: {
    formatApiError,
    parseContent(resp) {
      const d = resp?.data || {};
      return d.Content || d.content || [];
    },
    /** Texto del capítulo en tabla (API antes devolvía glosa errónea por ISNULL con 0). */
    etiquetaCapitulo(row) {
      const c = row.Capitulo;
      if (!c) return '—';
      const id = c.Id;
      const g = (c.Glosa != null ? String(c.Glosa) : '').trim();
      if (g && g !== '0') return g;
      if (id != null && Number(id) > 0) return `Capítulo #${id}`;
      return '—';
    },
    async cargarCombos() {
      const [c, a] = await Promise.all([CursoService.get(), AsignaturaService.get()]);
      this.cursos = c.data || [];
      this.asignaturas = a.data || [];
    },
    async cargarCapitulosFiltro() {
      if (!this.puedeFiltrarCapitulos) return;
      try {
        const r = await CapituloUnidadService.listarCapitulos(1, 500, {
          idCurso: this.selectedCursoId,
          idAsignatura: this.selectedAsignaturaId,
        });
        this.capitulosFiltro = this.parseContent(r);
      } catch (e) {
        console.error(e);
        this.capitulosFiltro = [];
      }
    },
    filtrosListado() {
      const f = {};
      const g = (this.filtroGlosa || '').trim();
      if (g) f.glosa = `%${g}%`;
      if (this.selectedCursoId) f.idCurso = this.selectedCursoId;
      if (this.selectedAsignaturaId) f.idAsignatura = this.selectedAsignaturaId;
      if (this.selectedCapituloId) f.idCapitulo = this.selectedCapituloId;
      return f;
    },
    async aplicarFiltros() {
      this.page = 1;
      await this.cargarLista();
    },
    async cargarLista() {
      this.loading = true;
      try {
        const resp = await VideoService.listarPaginado(this.page, this.pageSize, this.filtrosListado());
        const d = resp.data || {};
        this.videos = this.parseContent(resp);
        this.totalElements = d.TotalElements ?? d.totalElements ?? this.videos.length;
        const tp = d.TotalPage ?? d.totalPage;
        this.totalPages = Math.max(
          1,
          tp != null ? Math.ceil(Number(tp)) : Math.ceil(this.totalElements / this.pageSize) || 1
        );
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error al cargar videos');
        this.videos = [];
      } finally {
        this.loading = false;
      }
    },
    paginaAnterior() {
      if (this.page <= 1) return;
      this.page -= 1;
      this.cargarLista();
    },
    paginaSiguiente() {
      if (!this.hayMasPaginas) return;
      this.page += 1;
      this.cargarLista();
    },
    async abrirNuevo() {
      if (this.puedeFiltrarCapitulos) {
        await this.cargarCapitulosFiltro();
      }
      this.tituloVideo = 'Nuevo video';
      const capInicial = this.selectedCapituloId || null;
      this.form = {
        Id: 0,
        Glosa: '',
        Vigente: true,
        Orden: 1,
        IdVimeo: 0,
        capituloId: capInicial,
        capituloIdInicial: null,
      };
      this.dialogVideo = true;
    },
    async abrirEditar(row) {
      if (this.puedeFiltrarCapitulos) {
        await this.cargarCapitulosFiltro();
      }
      this.tituloVideo = 'Editar video';
      const capId = row.Capitulo?.Id != null && Number(row.Capitulo.Id) > 0 ? Number(row.Capitulo.Id) : null;
      try {
        const r = await VideoService.getById(row.Id);
        const v = r.data || row;
        const capDesdeDetalle =
          v.Capitulo?.Id != null && Number(v.Capitulo.Id) > 0 ? Number(v.Capitulo.Id) : capId;
        this.form = {
          Id: v.Id,
          Glosa: v.Glosa || '',
          Vigente: v.Vigente !== false,
          Orden: v.Orden != null ? v.Orden : 1,
          IdVimeo: v.IdVimeo != null ? v.IdVimeo : 0,
          capituloId: capDesdeDetalle,
          capituloIdInicial: capDesdeDetalle,
        };
      } catch {
        this.form = {
          Id: row.Id,
          Glosa: row.Glosa || '',
          Vigente: !!row.Vigente,
          Orden: row.Orden || 1,
          IdVimeo: row.IdVimeo || 0,
          capituloId: capId,
          capituloIdInicial: capId,
        };
      }
      this.dialogVideo = true;
    },
    payloadVideo() {
      return {
        Id: this.form.Id || 0,
        Glosa: (this.form.Glosa || '').trim(),
        Vigente: !!this.form.Vigente,
        Orden: Number(this.form.Orden ?? 1),
        IdVimeo: Number(this.form.IdVimeo ?? 0),
      };
    },
    async guardarVideo() {
      const p = this.payloadVideo();
      if (!p.Glosa) {
        alert('Ingresa la glosa del video.');
        return;
      }
      try {
        const resp = await VideoService.guardar(p);
        const guardado = resp.data;
        const nuevoId = guardado?.Id ?? p.Id;
        const vid = this.form.Id === 0 ? nuevoId : this.form.Id;
        if (vid && this.form.capituloId) {
          const esNuevo = this.form.Id === 0;
          const mismoCapQueAlAbrir =
            this.form.capituloIdInicial === this.form.capituloId ||
            (this.form.capituloIdInicial == null && this.form.capituloId == null);
          if (esNuevo || !mismoCapQueAlAbrir) {
            await VideoService.relacionarVideoCapitulo(vid, this.form.capituloId);
          }
        }
        this.dialogVideo = false;
        await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error al guardar');
      }
    },
    async desactivar(row) {
      if (!confirm(`¿Desactivar el video ${row.Id}?`)) return;
      try {
        await VideoService.guardar({
          Id: row.Id,
          Glosa: row.Glosa,
          Vigente: false,
          Orden: row.Orden ?? 1,
          IdVimeo: row.IdVimeo ?? 0,
        });
        await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error');
      }
    },
    /** Misma base pública que otras vistas del admin (recursos estáticos v1). */
    urlCaratulaPublica(link) {
      if (link == null || String(link).trim() === '') return '';
      const s = String(link).trim();
      if (/^https?:\/\//i.test(s)) return s;
      const base = 'https://estudyoo.cl/v1/';
      return base.replace(/\/+$/, '/') + s.replace(/^\/+/, '');
    },
    /** Query anti-caché: por video tras actualizar; si no, valor global (inicializado en mounted). */
    revisionCaratula(videoId) {
      if (videoId != null && this.caratulaThumbRevision[videoId] != null) {
        return this.caratulaThumbRevision[videoId];
      }
      return this.caratulaCacheBust;
    },
    urlCaratulaConBust(link, videoId = null) {
      const u = this.urlCaratulaPublica(link);
      if (!u) return '';
      const rev = this.revisionCaratula(videoId);
      const sep = u.includes('?') ? '&' : '?';
      return `${u}${sep}cv=${rev}`;
    },
    /** No usar visibility:hidden: un fallo dejaba la miniatura invisible para siempre. */
    onCaratulaImgError(e) {
      const el = e?.target;
      if (!el || el.tagName !== 'IMG') return;
      el.classList.add('caratula-img-err');
      el.alt = 'Sin vista';
    },
    abrirPortada(row) {
      this.portadaVideoId = row.Id;
      this.portadaRow = row;
      this.archivoPortada = null;
      this.portadaFileInputKey += 1;
      this.dialogPortada = true;
    },
    onFilePortada(e) {
      const f = e.target.files && e.target.files[0];
      if (!f) {
        this.archivoPortada = null;
        return;
      }
      const okMime = f.type === 'image/png' || f.type === 'image/jpeg';
      const name = (f.name || '').toLowerCase();
      const okExt = name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
      if (okMime || okExt) {
        this.archivoPortada = f;
      } else {
        this.archivoPortada = null;
        alert('Solo PNG o JPEG.');
      }
    },
    onCaratulaImgLoad(e) {
      const el = e?.target;
      if (el?.classList) el.classList.remove('caratula-img-err');
    },
    async subirPortada() {
      if (!this.archivoPortada) {
        alert('Selecciona un archivo (PNG o JPEG).');
        return;
      }
      this.subiendoPortada = true;
      try {
        const resp = await VideoService.subirPortada(this.portadaVideoId, this.archivoPortada);
        const body = resp?.data || {};
        const linkNuevo = body.LinkImagenPortada || body.linkImagenPortada;
        const idVid = this.portadaVideoId;
        const ts = Date.now();
        // Por video + query cv=: el CDN/navegador suele cachear por URL sin distinguir otros videos.
        this.caratulaThumbRevision = { ...this.caratulaThumbRevision, [idVid]: ts };
        this.caratulaCacheBust = ts;
        if (linkNuevo && idVid != null) {
          this.videos = this.videos.map((v) =>
            v.Id === idVid ? { ...v, LinkImagenPortada: linkNuevo } : v
          );
          if (this.portadaRow && this.portadaRow.Id === idVid) {
            this.portadaRow = { ...this.portadaRow, LinkImagenPortada: linkNuevo };
          }
        }
        this.archivoPortada = null;
        this.dialogPortada = false;
        await this.cargarLista();
      } catch (e) {
        alert(this.formatApiError(e, 'Error al subir la carátula'));
      } finally {
        this.subiendoPortada = false;
      }
    },
    abrirVincular(row) {
      if (!this.puedeFiltrarCapitulos) {
        alert('Selecciona curso y asignatura arriba para listar capítulos.');
        return;
      }
      this.vincularVideoId = row.Id;
      this.vincularCapituloId = this.selectedCapituloId || null;
      this.dialogVincular = true;
    },
    async confirmarVincular() {
      if (!this.vincularCapituloId) {
        alert('Selecciona un capítulo.');
        return;
      }
      try {
        await VideoService.relacionarVideoCapitulo(this.vincularVideoId, this.vincularCapituloId);
        this.dialogVincular = false;
        await this.cargarLista();
      } catch (e) {
        alert(e.response?.data?.Message || e.message || 'Error al vincular');
      }
    },
  },
  async mounted() {
    this.caratulaCacheBust = Date.now();
    await this.cargarCombos();
    await this.cargarCapitulosFiltro();
    await this.cargarLista();
  },
};
</script>

<style scoped>
.caratula-thumb-wrap {
  width: 3.25rem;
  height: 2.25rem;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--surface-border, #dee2e6);
  background: var(--surface-ground, #f8f9fa);
}
.caratula-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.caratula-thumb.caratula-img-err,
.caratula-preview.caratula-img-err {
  opacity: 0.35;
}
.caratula-thumb.caratula-img-err {
  object-fit: none;
}
.caratula-preview {
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: 6px;
  display: block;
}
</style>
